// Profit & Loss API — /admin/pnl/* (backend/routers/pnl.py).
// Contract: docs/superpowers/specs/2026-11-03-profit-and-loss-design.md.
// Money fields come back as numbers or null (unknown); every response carries
// missing_inputs: [string]. Never coerce null to 0 on this side.
import api, { getXeroConnectUrl } from './api'

const BASE = '/admin/pnl'

/** { allowed: bool } — drives the sidebar item and the page gate. */
export async function getPnlAccess() {
  const { data } = await api.get(`${BASE}/access`)
  return data
}

/** { xero_connected, missing_scopes, last_refresh, daily_computed_to, xero_months, timesheets_to } */
export async function getPnlStatus() {
  const { data } = await api.get(`${BASE}/status`)
  return data
}

/** granularity: 'month' | 'quarter' | 'year'; anchor: 'YYYY-MM-DD'. */
export async function getPnlStatement({ granularity, anchor }) {
  const { data } = await api.get(`${BASE}/statement`, { params: { granularity, anchor } })
  return data
}

export async function getPnlDaily({ from, to }) {
  const { data } = await api.get(`${BASE}/daily`, { params: { from, to } })
  return data
}

export async function getPnlClients({ from, to }) {
  const { data } = await api.get(`${BASE}/clients`, { params: { from, to } })
  return data
}

/** One day's orders, computed live. client_id / loss_only are optional. */
export async function getPnlOrders({ date, client_id, loss_only, page = 1, page_size = 50 }) {
  const params = { date, page, page_size }
  if (client_id) params.client_id = client_id
  if (loss_only) params.loss_only = true
  const { data } = await api.get(`${BASE}/orders`, { params })
  return data
}

/** { targets: { pbit_month, pbit_per_order, gross_margin } } */
export async function getPnlSettings() {
  const { data } = await api.get(`${BASE}/settings`)
  return data
}

export async function updatePnlSettings(payload) {
  const { data } = await api.put(`${BASE}/settings`, payload)
  return data
}

/** { accounts: [{ account_id, code, name, type, line, is_default }], lines: [...] } */
export async function getPnlAccounts() {
  const { data } = await api.get(`${BASE}/accounts`)
  return data
}

export async function updatePnlAccount(accountId, line) {
  const { data } = await api.put(`${BASE}/accounts/${encodeURIComponent(accountId)}`, { line })
  return data
}

/** Backfill / recompute in a background thread. Months as 'YYYY-MM'. → { accepted: true } */
export async function refreshPnl({ from_month, to_month } = {}) {
  const body = {}
  if (from_month) body.from_month = from_month
  if (to_month) body.to_month = to_month
  const { data } = await api.post(`${BASE}/refresh`, body)
  return data
}

/**
 * Reconnect Xero WITHOUT disconnecting (disconnect deletes the token row and
 * the invoice item codes). Fetches the consent URL with return_to so the
 * callback lands back on the Setup tab, then sends the browser there.
 */
export async function reconnectXeroForPnl() {
  const url = await getXeroConnectUrl('/admin/profit-loss/setup')
  window.location.href = url
}
